import { useCallback, useLayoutEffect } from 'react';
import { getReactNativeDeviceInfoLibrary } from '@/utils/deviceInfoDynamicImport';
import { useErrorBoundary } from 'react-error-boundary';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { isRootedExperimentalAsync } from 'expo-device';
import Bowser from 'bowser';
import { constants } from '@/config/constants';

// https://browser-update.org/browsers.html
const VALID_BROWSERS = {
  chrome: process.env.EXPO_PUBLIC_VALID_BROWSERS_CHROME || '>126',
  firefox: process.env.EXPO_PUBLIC_VALID_BROWSERS_FIREFOX || '>126',
  safari: process.env.EXPO_PUBLIC_VALID_BROWSERS_SAFARI || '>16.5',
  edge: process.env.EXPO_PUBLIC_VALID_BROWSERS_EDGE || '>126',
};

const MIN_IOS_VERSION = 15;
const MIN_ANDROID_SDK = 24;

export function useStartupChecks() {
  const { showBoundary } = useErrorBoundary();

  const checkIfDeviceIsRooted = useCallback(() => {
    isRootedExperimentalAsync().then((rooted) => {
      if (rooted) {
        showBoundary(new Error('ROOTED_DEVICE_ERROR'));
      }
    });
  }, []);

  const checkIfDeviceIsProtected = useCallback(async () => {
    if (!constants.isRunningInExpoGo) {
      const deviceInfo = await getReactNativeDeviceInfoLibrary();

      if (await AsyncStorage.getItem('unprotectedDevice')) {
        return;
      }

      const isSecure = await deviceInfo.isPinOrFingerprintSet();
      if (!isSecure) {
        showBoundary(new Error('UNPROTECTED_DEVICE_ERROR'));
      }
    }
  }, []);

  const checkIfBrowserIsOutdated = useCallback(() => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    const validBrowserNames = Object.keys(VALID_BROWSERS);
    const currentBrowserName = browser.getBrowserName().toLowerCase();
    if (!validBrowserNames.includes(currentBrowserName)) {
      return false;
    }
    if (browser.satisfies(VALID_BROWSERS)) {
      return;
    }
    if (sessionStorage.getItem('unsafeBrowserContinue')) {
      return;
    }

    showBoundary(new Error('OUTDATED_BROWSER_ERROR'));
  }, []);

  const checkOsVersion = useCallback(async () => {
    if (Platform.OS === 'ios') {
      const userOptedToContinue = await AsyncStorage.getItem(
        'unsupportedIosVersionContinue',
      );
      if (userOptedToContinue === 'true') {
        return;
      }
      const iosVersion = parseFloat(Platform.Version);
      if (iosVersion < MIN_IOS_VERSION) {
        showBoundary(new Error('UNSUPPORTED_IOS_VERSION_ERROR'));
      }
    } else if (Platform.OS === 'android') {
      const userOptedToContinue = await AsyncStorage.getItem(
        'unsupportedAndroidVersionContinue',
      );
      if (userOptedToContinue === 'true') {
        return;
      }
      const androidVersion = Platform.Version;
      if (androidVersion < MIN_ANDROID_SDK) {
        showBoundary(new Error('UNSUPPORTED_ANDROID_VERSION_ERROR'));
      }
    }
  }, []);

  useLayoutEffect(() => {
    if (Platform.OS === 'web') {
      checkIfBrowserIsOutdated();
    } else {
      checkIfDeviceIsRooted();
      checkOsVersion();
      if (constants.unprotectedDeviceErrorEnabled) {
        checkIfDeviceIsProtected();
      }
    }
  }, []);
}
